import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback, useEffect, useRef, useState,
} from "react";
import { toast } from "sonner";
import { ACCOUNTING_QUERY_KEYS } from "../../configs/queryKeys";
import { useSettlementsQuery, useUpdateSettlement } from "../../hooks/useAccountingQueries";
import { accountingService } from "../../services/accounting.service";
import { extractApiErrorMessage } from "./utils";
import type { Settlement } from "../../types/accounting.types";

type UseMovementSettlementsTabParams = {
  bankMovementId: string | null
  enabled: boolean
  isMovementLocked: boolean
};

export const useMovementSettlementsTab = ({
  bankMovementId,
  enabled,
  isMovementLocked,
}: UseMovementSettlementsTabParams) => {
  const queryClient = useQueryClient();

  // -- Associated settlements --
  const associatedQuery = useSettlementsQuery(
    { bankMovementIdEq: bankMovementId ?? undefined, limit: 100 },
    enabled && Boolean(bankMovementId),
  );
  const associatedSettlements = associatedQuery.data?.settlements ?? [];
  const isLoadingAssociated = associatedQuery.isLoading;

  // -- Search for unassociated settlements --
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Settlement[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchRef = useRef("");

  // Reset search when tab becomes inactive or movement changes
  useEffect(() => {
    if (!enabled) {
      setSearchQuery("");
      setSearchResults([]);
      lastSearchRef.current = "";
    }
  }, [enabled]);

  const runSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const result = await accountingService.listSettlements({
        bankMovementIdNull: true,
        paymentMethodNotEq: "Compensation",
        search: query.trim(),
        limit: 20,
      });
      setSearchResults(result.settlements);
    }
    catch {
      setSearchResults([]);
    }
    finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    lastSearchRef.current = value;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(() => {
      void runSearch(value);
    }, 300);
  }, [runSearch]);

  // -- Associate / Disassociate --
  const updateSettlement = useUpdateSettlement();
  const [associatingId, setAssociatingId] = useState<string | null>(null);
  const [disassociatingId, setDisassociatingId] = useState<string | null>(null);

  const handleAssociate = useCallback(async (settlementId: string) => {
    if (!bankMovementId || isMovementLocked) return;
    setAssociatingId(settlementId);
    try {
      await updateSettlement.mutateAsync({
        id: settlementId,
        payload: { bankMovementId },
      });
      // Remove from search results
      setSearchResults((prev) => prev.filter((s) => s.id !== settlementId));
      // Invalidate associated list + allocations (settlement's allocations are attached to the movement)
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      toast.success("Règlement associé au mouvement");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setAssociatingId(null);
    }
  }, [bankMovementId, isMovementLocked, updateSettlement, queryClient]);

  const handleDisassociate = useCallback(async (settlementId: string) => {
    if (isMovementLocked) return;
    setDisassociatingId(settlementId);
    try {
      await updateSettlement.mutateAsync({
        id: settlementId,
        payload: { bankMovementId: null },
      });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      toast.success("Règlement dissocié du mouvement");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setDisassociatingId(null);
    }
  }, [isMovementLocked, updateSettlement, queryClient]);

  const [deletingSettlementId, setDeletingSettlementId] = useState<string | null>(null);

  const handleDeleteSettlement = useCallback(async (settlementId: string) => {
    if (isMovementLocked) return;
    setDeletingSettlementId(settlementId);
    try {
      await accountingService.deleteSettlement(settlementId, { keepAllocations: true });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      // Settlement deletion can change the status of linked entities
      queryClient.invalidateQueries({ queryKey: ["refund-requests"] });
      queryClient.invalidateQueries({ queryKey: ["ecom-orders"] });
      toast.success("Règlement supprimé — les écritures restent sur le mouvement");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
    finally {
      setDeletingSettlementId(null);
    }
  }, [isMovementLocked, queryClient]);

  return {
    associatedSettlements,
    isLoadingAssociated,
    searchQuery,
    handleSearchChange,
    searchResults,
    isSearching,
    associatingId,
    disassociatingId,
    handleAssociate,
    handleDisassociate,
    deletingSettlementId,
    handleDeleteSettlement,
  };
};
